/**
 * Copyright 2023 Design Barn Inc.
 */

import { GrowthBook, GrowthBookProvider, useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import React, { useCallback, useEffect } from 'react';

export enum FeatureFlags {
  CLEVERTAP_COHORTS_ENABLED = 'platform_auth_enable_clevertap_cohorts',
  CLEVERTAP_TEST_GROUPS_ENABLED = 'platform_auth_enable_clevertap_test_groups',
  // this flag enables the email verification flow feature entirely
  ENABLE_EMAIL_VERIFICATION_FEATURE = 'platform_auth_enable_email_verification_feature',
  // this flag determines whether the email verification dialog step is shown to a user that has not yet verified their email
  ENABLE_EMAIL_VERIFICATION_STEP = 'platform_auth_enable_email_verification_step',
}

const gb = new GrowthBook({
  apiHost: process.env['NEXT_PUBLIC_GROWTHBOOK_ENDPOINT'],
  // eslint-disable-next-line no-secrets/no-secrets
  clientKey: process.env['NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY'],
  // Enable easier debugging during development
  enableDevMode: process.env.NODE_ENV === 'development',
});

export interface UseFeatureFlags {
  identify: (identity: { id: string; traits: { email: string } }) => void;
  useFeatureIsOn: typeof useFeatureIsOn;
  useFeatureValue: typeof useFeatureValue;
}

export const useFeatureFlags = (): UseFeatureFlags => {
  const identify = useCallback((identity: { id: string; traits: { email: string } }) => {
    gb.setAttributes({
      id: identity.id,
      email: identity.traits.email,
      loggedIn: true,
      web: true,
    });
  }, []);

  return {
    identify,
    useFeatureIsOn,
    useFeatureValue,
  };
};

export const getGrowthBookInstance = (): typeof gb => gb;

export const FeatureFlagProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  useEffect(() => {
    // Load features from the GrowthBook API
    // eslint-disable-next-line no-secrets/no-secrets
    if (process.env['NEXT_PUBLIC_GROWTHBOOK_ENDPOINT'] && process.env['NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY']) {
      gb.loadFeatures({ autoRefresh: true });
    } else {
      // eslint-disable-next-line no-console
      console.warn('Loading without feature flags');
    }
  }, []);

  return <GrowthBookProvider growthbook={gb}>{children}</GrowthBookProvider>;
};
